import { useState, useEffect, useRef } from 'react';
import {
  Box, Card, CardContent, Button, Typography, Alert, Grid,
  CircularProgress, Link, TextField,
} from '@mui/material';
import { MarkEmailRead } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL;

const VerifyEmail = () => {
  const [params]     = useSearchParams();
  const [email,      setEmail]      = useState(params.get('email') || '');
  const [codes,      setCodes]      = useState(['', '', '', '', '', '']);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const [loading,    setLoading]    = useState(false);
  const [cooldown,   setCooldown]   = useState(0);
  const inputRefs = useRef([]);
  const navigate  = useNavigate();

  // Countdown timer for resend
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleDigit = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...codes];
    next[i] = val;
    setCodes(next);
    if (val && i < 5) inputRefs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !codes[i] && i > 0) {
      inputRefs.current[i - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (paste.length === 6) {
      setCodes(paste.split(''));
      inputRefs.current[5]?.focus();
      e.preventDefault();
    }
  };

  const code = codes.join('');

  const handleVerify = async () => {
    if (code.length < 6) { setError('Please enter the full 6-digit code'); return; }
    setLoading(true); setError(''); setSuccess('');
    try {
      const res = await axios.post(`${API}/api/auth/verify-email`, { email, code });
      setSuccess(res.data.message);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setCooldown(60); setError(''); setSuccess('');
    try {
      const res = await axios.post(`${API}/api/auth/resend-verification`, { email });
      setSuccess(res.data.message);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not resend code.');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f4f6f8' }}>
      <Box sx={{ width: '100%', maxWidth: 420, px: 2 }}>
        <Card elevation={4} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
              <MarkEmailRead color="primary" />
              <Typography variant="h6" fontWeight={700}>Verify your email</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" mb={3}>
              We sent a 6-digit code to <strong>{email || 'your email'}</strong>.
              Enter it below. The code expires in 15 minutes.
            </Typography>

            {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success} Redirecting…</Alert>}

            {/* Email field (editable in case URL didn't carry it) */}
            {!params.get('email') && (
              <TextField label="Email Address" value={email} onChange={e => setEmail(e.target.value)}
                fullWidth size="small" sx={{ mb: 2.5 }} />
            )}

            {/* 6-box OTP input */}
            <Box display="flex" gap={1} justifyContent="center" mb={3} onPaste={handlePaste}>
              {codes.map((digit, i) => (
                <TextField
                  key={i}
                  value={digit}
                  onChange={e => handleDigit(i, e.target.value)}
                  onKeyDown={e => handleKeyDown(i, e)}
                  inputRef={el => (inputRefs.current[i] = el)}
                  inputProps={{ maxLength: 1, style: { textAlign: 'center', fontSize: 26, fontWeight: 900, padding: '10px 0', width: 40 } }}
                  sx={{ width: 50 }}
                />
              ))}
            </Box>

            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <Button variant="contained" fullWidth size="large"
                  onClick={handleVerify} disabled={loading || code.length < 6}
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}>
                  {loading ? 'Verifying…' : 'Verify Email'}
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Button variant="outlined" fullWidth onClick={handleResend} disabled={cooldown > 0}>
                  {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend Code'}
                </Button>
              </Grid>
            </Grid>

            <Typography variant="body2" color="text.secondary" align="center" mt={2.5}>
              <Link component="button" onClick={() => navigate('/login')}>Back to Sign In</Link>
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default VerifyEmail;
